// src/pages/VerifyEmailPage.tsx
import { useEffect, useState } from 'react';
import { Mail, CheckCircle, XCircle, Clock, ArrowLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useRouter } from '../components/Router';
import { getListingData, clearListingData } from '../utils/listingStorage';

export function VerifyEmailPage() {
  const { navigate } = useRouter();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Vérification en cours...');
  const [redirectTo, setRedirectTo] = useState('/');

  useEffect(() => {
    const verifyEmail = async () => {
      const params = new URLSearchParams(window.location.search);
      const redirectUrl = params.get('redirect');
      if (redirectUrl) {
        setRedirectTo(redirectUrl);
      }

      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const errorCode = hashParams.get('error_code');
      const type = hashParams.get('type');

      if (errorCode) {
        setStatus('error');
        let errorMessage = 'Une erreur est survenue lors de la vérification.';

        if (errorCode === 'otp_expired') {
          errorMessage = 'Le lien de vérification a expiré. Veuillez vous réinscrire.';
        } else if (errorCode === 'access_denied') {
          errorMessage = 'Accès refusé. Veuillez vérifier le lien.';
        }

        setMessage(errorMessage);
        return;
      }

      if (type === 'signup') {
        try {
          const { data: { session }, error } = await supabase.auth.getSession();

          if (error || !session) {
            setStatus('error');
            setMessage('Session invalide. Veuillez vous reconnecter.');
            return;
          }

          handleEmailVerified();
        } catch (err) {
          setStatus('error');
          setMessage('Erreur de vérification.');
        }
      } else {
        setStatus('error');
        setMessage('Lien de vérification invalide.');
      }
    };

    verifyEmail();
  }, []);

  const handleEmailVerified = async () => {
    try {
      // Get the current user session
      const { data: { user }, error } = await supabase.auth.getUser();
      
      if (error) throw error;

      if (user?.email_confirmed_at) {
        // Email is confirmed
        const pendingListing = getListingData();
        
        if (pendingListing) {
          // If there's a pending listing, submit it
          setMessage('Email vérifié. Publication de votre annonce en cours...');
          setStatus('loading');
          
          try {
            const { error: listingError } = await supabase
              .from('listings')
              .insert([
                {
                  user_id: user.id,
                  brand: pendingListing.brand,
                  model: pendingListing.model,
                  year: parseInt(pendingListing.year),
                  mileage: parseInt(pendingListing.mileage),
                  fuel_type: pendingListing.fuelType,
                  transmission: pendingListing.transmission,
                  color: pendingListing.color,
                  price: parseFloat(pendingListing.price),
                  description: pendingListing.description,
                  location: pendingListing.location,
                  condition: pendingListing.condition === 'Neuf' ? 'new' : 'used',
                  contact_phone: pendingListing.contactPhone,
                  contact_email: pendingListing.contactEmail,
                  images: pendingListing.imageUrls,
                  status: 'pending' // Listing starts as pending review
                }
              ]);

            if (listingError) throw listingError;

            // Clear the saved listing data
            clearListingData();
            
            setStatus('success');
            setMessage('Annonce publiée avec succès ! Vous allez être redirigé vers Mon Garage.');
            
            // Redirect to my listings after a short delay
            setTimeout(() => {
              navigate('/mes-annonces');
            }, 3000);
          } catch (err: any) {
            console.error('Error publishing listing:', err);
            setStatus('error');
            setMessage('Erreur lors de la publication de l\'annonce. L\'email est vérifié, mais veuillez réessayer de publier.');
          }
        } else {
          // No pending listing, just confirm email verification
          setStatus('success');
          setMessage('Votre email a été vérifié. Vous allez être redirigé vers la page de publication.');
          
          // Redirect to submit listing page after a short delay
          setTimeout(() => {
            navigate(redirectTo);
          }, 3000);
        }
      } else {
        // Confirmation failed or user not logged in
        setStatus('error');
        setMessage('Votre email n\'a pas encore été vérifié. Veuillez vérifier votre boîte mail et cliquer sur le lien.');
      }
    } catch (err: any) {
      console.error('Verification error:', err);
      setStatus('error');
      setMessage('Une erreur est survenue lors de la vérification. Veuillez réessayer.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
      <div className="max-w-lg w-full bg-white rounded-3xl shadow-xl p-10 text-center animate-in fade-in duration-500">
        
        {/* Loading State */}
        {status === 'loading' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#D4452C] border-t-transparent mx-auto mb-6"></div>
            <Mail className="h-12 w-12 text-[#D4452C] mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-3 tracking-tight">Vérification en cours</h2>
            <p className="text-gray-500 text-base">{message}</p>
          </>
        )}

        {/* Success State */}
        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-green-200">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3 tracking-tight">
              {message.includes('publiée') ? 'Annonce publiée !' : 'Email vérifié !'}
            </h2>
            <p className="text-gray-600 mb-8 font-medium leading-relaxed">{message}</p>
            
            {message.includes('publiée') && (
              <div className="inline-flex items-center text-sm font-semibold text-gray-500 bg-gray-50 px-4 py-2 rounded-full">
                <Clock className="h-4 w-4 mr-2" />
                Redirection en cours...
              </div>
            )}
          </>
        )}

        {/* Error State */}
        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-red-200">
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3 tracking-tight">Échec de la vérification</h2>
            <p className="text-gray-600 mb-8 font-medium leading-relaxed">{message}</p>
            <a
              href="/registre"
              className="inline-block w-full bg-[#D4452C] text-white py-3.5 rounded-xl font-bold hover:bg-[#b83a24] shadow-lg shadow-orange-900/10 transition-all"
            >
              Réessayer de s'inscrire
            </a>
          </>
        )}
        
        <div className="mt-8 pt-6 border-t border-gray-100">
          <a 
            href="/" 
            className="flex items-center justify-center text-sm font-semibold text-gray-500 hover:text-[#D4452C] transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour à l'accueil
          </a>
        </div>
      </div>
    </div>
  );
}